import { NormalizeOAS, OASRequestParams, OASResponse } from 'fets';
import type assetLibraryOpenApi from '../asset-library-open-api';
import { AssetLibraryFetsApiClient } from '../asset-library-fets-client';
import { assertNever } from '../utils';
import { Guid } from '../../api/public/utils/Guid';

const PATH = '/api/v2/asset/company/{companyId}/copy/to/{targetDirectoryId}';

type Params = {
  companyId: Guid;
  targetDirectoryId: Guid;
  assetIds: Guid[];
};

/** The asset library's own AssetCopyProblem; `None` means the asset was copied. */
export type AssetCopyProblem = 'None' | 'Archived' | 'ProcessingInProgress' | 'ProcessingFailed' | 'NotFound';

export type NotCopiedAsset = {
  assetId: Guid;
  reason: Exclude<AssetCopyProblem, 'None'>;
  /** Null when the asset was not found. */
  name: string | null;
};

export type CopiedAssets = {
  /** The ids of the copies, not of the originals. */
  assetIds: Guid[];
  notCopiedAssets: NotCopiedAsset[];
};

type Result = CopiedAssets;

export type ApiFunction = (params: Params, signal?: AbortSignal) => Promise<Result>;

export const createCopyAssets = (apiClient: AssetLibraryFetsApiClient): ApiFunction => {
  const copyAssets = async (params: Params, signal?: AbortSignal): Promise<Result> => {
    const response = await apiClient[PATH].post({
      ...convertToRequest(params),
      ...(signal && { signal }),
    });

    return convertResponse(response);
  };

  return copyAssets;
};

type Request = OASRequestParams<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'post'>;
type Response = OASResponse<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'post'>;

const convertToRequest = (params: Params): Request => ({
  params: { companyId: params.companyId, targetDirectoryId: params.targetDirectoryId },
  json: { assetIds: params.assetIds },
});

const convertResponse = async (response: Response): Promise<Result> => {
  const responseStatus = response.status;
  if (responseStatus !== 200) {
    return assertNever(responseStatus);
  }

  const data = await response.json();
  return {
    assetIds: data.assetIds,
    // The server never reports a copied asset here, `None` only narrows the type.
    notCopiedAssets: data.notCopiedAssets.flatMap(({ assetId, reason, name }) =>
      reason === 'None' ? [] : [{ assetId, reason, name: name ?? null }]
    ),
  };
};
